import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
// import { AiInterviewState, ConversationPayload, EndSessionPayload, InterviewSessionPayload, InterviewSessionResponse } from '@edtech-ui/core/models/ai-interview/ai-interview-session';
// import { environment } from 'environments/environment';
import { InterviewSession } from '../models/requestModels/AiInterviewState';
import { environment } from 'environments/environment';
const API_BASE_URL = environment.hrmsApiUrl;
const AI_INTERVIEW_URL = environment.aiScreeningInterviewManagementUrl;
const statusUpdate = `${API_BASE_URL}/ai/session/status`;
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, catchError, Observable, of, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';
import {
  ProctoringConfig,
  ProctoringEvent,
  ProctoringReport,
  ProctoringViolationType,
  ViolationSeverity
} from '../models/proctoring.models';

@Injectable({
  providedIn: 'root'
})
export class AiInterviewSessionService {
  private baseUrl = AI_INTERVIEW_URL;

  // Production-grade mock toggle: Only use mock on localhost/dev environments
  private useMock = typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1'));

  constructor(private readonly http: HttpClient, private toast: ToastrService) { }

  private state$ = new BehaviorSubject<any>(this.load()); // AiInterviewState
  readonly stateChanges$ = this.state$.asObservable();

  get value(): any { // AiInterviewState
    return this.state$.value;
  }

  update(patch: Partial<any>) { // AiInterviewState
    const updated = { ...this.value, ...patch };
    this.state$.next(updated);
    sessionStorage.setItem('ai_session_state', JSON.stringify(updated));
  }

  reset() {
    sessionStorage.removeItem('ai_session_state');
    this.state$.next({});
  }

  private load(): any { //AiInterviewState
    try {
      const raw = sessionStorage.getItem('ai_session_state');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }

  loadFromStorage() {
    const raw = sessionStorage.getItem('ai_session_state');
    return raw ? JSON.parse(raw) : {};
  }

  updateConversation(payload: {
    session_id: string;
    conversation_json: { role: string; content: string; timestamp: string }[];
    voice_analysis_raw?: string;
  }): Observable<any> { //ApiBaseResponse
    const formData = new FormData();
    formData.append('session_id', payload.session_id);
    formData.append('conversation_json', JSON.stringify(payload.conversation_json));
    if (payload.voice_analysis_raw) {
      formData.append('voice_analysis_raw', payload.voice_analysis_raw);
    }

    const headers: any = {};
    const token = this.getAiSessionToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.post<any>( //ApiBaseResponse
      `${this.baseUrl}/realtime-interview/update-conversation`,
      formData,
      { headers }
    );
  }

  endInterview(payload: { session_id: string, interview_started_at?: string, interview_ended_at?: string }) {
    const formData = new FormData();
    formData.append('session_id', payload.session_id);
    if (payload.interview_started_at) {
      formData.append('interview_started_at', payload.interview_started_at);
    }
    if (payload.interview_ended_at) {
      formData.append('interview_ended_at', payload.interview_ended_at);
    }

    const headers: any = {};
    const token = this.getAiSessionToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.post<any>( //ApiBaseResponse
      `${this.baseUrl}/realtime-interview/end`,
      formData,
      { headers }
    );
  }

  evaluateInterview(session_id: string) {
    const url = `${this.baseUrl}/realtime-interview/evaluate`;
    const formData = new FormData();
    formData.append('session_id', session_id);



    const headers: any = {};
    const token = this.getAiSessionToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.post<any>(url, formData, { headers }).pipe( //ApiBaseResponse
      catchError((error) => {
        return throwError(() => error);
      })
    );
  }

  private getAiSessionToken(): string | null {
    return sessionStorage.getItem('ai_session_token');
  }

  updateSessionStatus(sessionId: string | number, status: string): Observable<any> {
    const token = this.getAiSessionToken();
    if (!token) {
      return throwError(() => new Error('AI session token not found'));
    }

    return this.http.patch(statusUpdate, {
      session_id: sessionId,
      status: status
    }, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  createReport(payload: {
    session_id: number;
    final_score: number;
    category_scores: number[];
    ai_summary: string;
  }): Observable<any> {
    const token = this.getAiSessionToken();
    if (!token) {
      return throwError(() => new Error('AI session token not found'));
    }

    return this.http.post(`${API_BASE_URL}/ai/report`, payload, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  getSessionDetails(): Observable<any> {
    const token = this.getAiSessionToken();
    if (!token) {
      return throwError(() => new Error('AI session token not found'));
    }

    return this.http.get(`${API_BASE_URL}/ai/session`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }

  getProctoringConfig(id: string = 'default'): Observable<any> {
    return this.http.get(`${this.baseUrl}/realtime-interview/proctoring/config`, {
      params: { id }
    });
  }

  uploadSnapshot(payload: {
    session_id: string;
    image: Blob;
    is_violation: boolean;
    violation_type: string;
    severity: string;
    reason: string;
    violation_count: number;
    video_timestamp?: string;
  }): Observable<any> {
    const formData = new FormData();
    formData.append('session_id', payload.session_id);
    formData.append('image', payload.image, `snapshot_${Date.now()}.png`);
    formData.append('is_violation', String(payload.is_violation));
    formData.append('violation_type', payload.violation_type);
    formData.append('severity', payload.severity);
    formData.append('reason', payload.reason);
    formData.append('violation_count', String(payload.violation_count));
    if (payload.video_timestamp) {
      formData.append('video_timestamp', payload.video_timestamp);
    }

    const headers: any = {};
    const token = this.getAiSessionToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.post<any>(
      `${this.baseUrl}/realtime-interview/snapshot`,
      formData,
      { headers }
    );
  }

  uploadRecording(sessionId: string, video: Blob): Observable<any> {
    const ext = video.type.includes('mp4') ? 'mp4' : 'webm';
    const formData = new FormData();
    formData.append('session_id', sessionId);
    formData.append('video', video, `interview_recording.${ext}`);

    const headers: any = {};
    const token = this.getAiSessionToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.post<any>(
      `${this.baseUrl}/realtime-interview/video`,
      formData,
      { headers }
    );
  }

  initiateMultipartUpload(sessionId: string, contentType: string = 'video/webm'): Observable<any> {
    const formData = new FormData();
    formData.append('session_id', sessionId);
    formData.append('content_type', contentType);

    const headers: any = {};
    const token = this.getAiSessionToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.post<any>(
      `${this.baseUrl}/realtime-interview/video/multipart/initiate`,
      formData,
      { headers }
    );
  }

  getMultipartPartUrl(sessionId: string, uploadId: string, objectKey: string, partNumber: number): Observable<any> {
    const headers: any = {};
    const token = this.getAiSessionToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let params = new HttpParams()
      .set('session_id', sessionId)
      .set('upload_id', uploadId)
      .set('object_key', objectKey)
      .set('part_number', partNumber.toString());

    return this.http.get<any>(
      `${this.baseUrl}/realtime-interview/video/multipart/part-url`,
      { headers, params }
    );
  }

  completeMultipartUpload(sessionId: string, uploadId: string, objectKey: string, parts: any[]): Observable<any> {
    const payload = {
      session_id: sessionId,
      upload_id: uploadId,
      object_key: objectKey,
      parts: parts
    };

    const headers: any = {};
    const token = this.getAiSessionToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return this.http.post<any>(
      `${this.baseUrl}/realtime-interview/video/multipart/complete`,
      payload,
      { headers }
    );
  }

}